import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Shield, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BindingLegalList } from '@/components/legal/BindingLegalList';
import { GateLegalStatusNote } from '@/components/binding/GateProposedNotice';
import { BAG_PRIVACY, BAG_LEGAL_EFFECTIVE } from '@/components/legal/bindingGateLegal';

const LAST_UPDATED = 'March 26, 2026';

const Section = ({ id, title, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="scroll-mt-24"
  >
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    <div className="space-y-3 text-sm text-gray-400 leading-relaxed">{children}</div>
  </motion.section>
);

export default function PrivacyPolicy() {
  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'info-collect', label: 'Information We Collect' },
    { id: 'how-use', label: 'How We Use Your Information' },
    { id: 'kyc', label: 'KYC / AML Verification' },
    { id: 'ai', label: 'AI-Powered Services' },
    { id: 'blockchain', label: 'Blockchain Data' },
    { id: 'payments', label: 'Payment Processing' },
    { id: 'sharing', label: 'Information Sharing' },
    { id: 'retention', label: 'Data Retention' },
    { id: 'security', label: 'Security' },
    { id: 'rights', label: 'Your Rights' },
    { id: 'cookies', label: 'Cookies & Tracking' },
    { id: 'children', label: 'Children\'s Privacy' },
    { id: 'public-ledger', label: 'Public Ledger and Anchors' },
    { id: 'changes', label: 'Changes to This Policy' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="container mx-auto px-6 pt-28 pb-20" data-testid="privacy-policy-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
              <p className="text-sm text-gray-500 mt-1">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
          <p className="text-gray-400 text-base leading-relaxed">
            Kojiru Technologies, Inc. ("Kojiru," "we," "us," or "our") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
            use the Kojiru platform, website, and related services (collectively, the "Platform").
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto flex gap-10">
          {/* Sidebar TOC: desktop only */}
          <aside className="hidden lg:block w-56 shrink-0 sticky top-28 self-start">
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block text-xs text-gray-500 hover:text-white py-1.5 transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-10">

            <Section id="overview" title="1. Overview">
              <p>
                Kojiru operates an AI-powered peer-to-peer lending marketplace built on the Base blockchain network.
                The Platform enables borrowers to request loans, lenders to fund loans in US Dollars and stablecoins,
                businesses to access accounts receivable financing, and all users to participate in staking,
                governance, and prediction features using the KOJIRU token (an ERC-20 digital asset).
              </p>
              <p>
                By accessing or using the Platform, you agree to the collection and use of information in accordance
                with this Privacy Policy. If you do not agree, please discontinue use of the Platform.
              </p>
            </Section>

            <Section id="info-collect" title="2. Information We Collect">
              <p className="font-medium text-gray-300">2.1 Information You Provide Directly</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Account Registration:</span> Email address, password (stored as a salted hash), and selected user role (Consumer, Lender, or Business).</li>
                <li><span className="text-gray-300">Profile Information:</span> Name, business name, EIN (for business users), and wallet address (if voluntarily connected).</li>
                <li><span className="text-gray-300">Loan Requests:</span> Loan amount, purpose, term, income information, employment status, and collateral details.</li>
                <li><span className="text-gray-300">Communications:</span> Messages sent through the Platform's contact or support features.</li>
              </ul>

              <p className="font-medium text-gray-300 mt-4">2.2 Information Collected Through Third-Party Services</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Identity Verification (KYC/KYB):</span> Government-issued photo ID, selfie images, date of birth, nationality, residential address, and document verification results: processed by our KYC partner, iDenfy (see Section 4).</li>
                <li><span className="text-gray-300">Payment Information:</span> Subscription billing data processed by Stripe. We do not store your full credit card number (see Section 7).</li>
              </ul>

              <p className="font-medium text-gray-300 mt-4">2.3 Information Collected Automatically</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Usage Data:</span> Pages visited, features used, timestamps, referral source, and session duration.</li>
                <li><span className="text-gray-300">Device Information:</span> Browser type, operating system, screen resolution, and IP address.</li>
                <li><span className="text-gray-300">Blockchain Data:</span> Public wallet addresses and on-chain transaction data associated with your use of KOJIRU tokens (see Section 6).</li>
              </ul>
            </Section>

            <Section id="how-use" title="3. How We Use Your Information">
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>To create and manage your account, authenticate your identity, and maintain Platform security.</li>
                <li>To facilitate peer-to-peer lending, including matching borrowers with lenders and processing loan requests and bids.</li>
                <li>To provide AI-powered credit analysis, bid suggestions, and match-score calculations (see Section 5).</li>
                <li>To process subscription payments and maintain billing records.</li>
                <li>To comply with legal and regulatory obligations, including anti-money laundering (AML) and know-your-customer (KYC) requirements under the Bank Secrecy Act (BSA).</li>
                <li>To detect, prevent, and respond to fraud, security threats, and injection attacks against our AI systems.</li>
                <li>To operate Platform features including staking, governance proposals, prediction markets, and daily challenges.</li>
                <li>To communicate important updates, security notices, and service changes.</li>
                <li>To improve the Platform through aggregated, de-identified analytics.</li>
              </ul>
            </Section>

            <Section id="kyc" title="4. KYC / AML Identity Verification">
              <p>
                Kojiru is required by U.S. federal law to verify the identity of users who participate in lending
                activities. We partner with <strong className="text-white">iDenfy</strong>, a certified identity
                verification provider, to perform Know Your Customer (KYC) and Know Your Business (KYB) checks.
              </p>
              <p className="font-medium text-gray-300 mt-3">What iDenfy Processes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Government-issued identification documents (passport, driver's license, national ID).</li>
                <li>Facial biometric data (selfie) for liveness verification and document-to-face matching.</li>
                <li>Name, date of birth, nationality, and residential address extracted from documents.</li>
                <li>For business users: company registration documents, articles of incorporation, and beneficial ownership information.</li>
              </ul>
              <p className="mt-3">
                Kojiru receives only the <em>verification result</em> (approved, denied, or pending) and a reference
                identifier from iDenfy. We do not store copies of your identity documents or biometric data on our
                servers. iDenfy processes this data in accordance with their own privacy policy, which is available
                at <a href="https://idenfy.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">idenfy.com/privacy-policy</a>.
              </p>
              <p className="mt-3">
                iDenfy retains verification data in compliance with applicable regulations and their data retention
                policies. You may exercise your data rights directly with iDenfy by contacting their Data Protection
                Officer.
              </p>
            </Section>

            <Section id="ai" title="5. AI-Powered Services">
              <p>
                Kojiru uses artificial intelligence to enhance the lending experience. Our AI services are powered
                by Google's Gemini language model, accessed through secure API integration.
              </p>
              <p className="font-medium text-gray-300 mt-3">AI Features and Data Usage:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Credit Analysis:</span> Evaluates borrower-provided information (loan amount, income, employment, collateral) to generate a risk assessment. No data is sent to external credit bureaus.</li>
                <li><span className="text-gray-300">Lender Bid Suggestions:</span> Analyzes loan request parameters to recommend competitive bid strategies for lenders.</li>
                <li><span className="text-gray-300">Match-Score Engine:</span> Calculates compatibility scores between borrowers and lenders based on reliability, yield alignment, volatility buffer, and social proof factors.</li>
                <li><span className="text-gray-300">Business Credit Analysis:</span> Evaluates accounts receivable quality, debtor concentration, and business health metrics for AR financing.</li>
              </ul>
              <p className="mt-3">
                <strong className="text-gray-300">AI Safeguards:</strong> All AI inputs are sanitized to prevent prompt injection attacks.
                AI outputs are validated and clamped within safe ranges (e.g., credit scores between 300-850,
                risk levels restricted to a defined whitelist). We maintain an audit log of all AI interactions
                for security monitoring. Users with repeated injection attempts are automatically flagged.
              </p>
              <p className="mt-3">
                AI-generated analysis is provided for informational purposes only and does not constitute financial
                advice. Lending decisions are made by individual lenders, not by AI systems.
              </p>
            </Section>

            <Section id="blockchain" title="6. Blockchain Data">
              <p>
                The Kojiru platform operates on the Base blockchain (an Ethereum Layer 2 network). Certain data
                associated with your use of KOJIRU tokens is inherently public due to the nature of blockchain technology.
              </p>
              <p className="font-medium text-gray-300 mt-3">Public Blockchain Data:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Wallet addresses used to interact with KOJIRU smart contracts.</li>
                <li>Token transfer, staking, unstaking, and reward claim transactions.</li>
                <li>Governance proposal creation and voting records.</li>
              </ul>
              <p className="mt-3">
                This data is recorded on a public, immutable ledger and <strong className="text-white">cannot be deleted or modified</strong> by
                Kojiru or any party. We do not publicly link your wallet address to your identity. However,
                on-chain data is visible to anyone using a blockchain explorer (e.g., Basescan).
              </p>
              <p className="mt-3">
                Kojiru's smart contracts are open-source and verified. They are owned by a multi-signature wallet
                and do not store any personal information on-chain.
              </p>
            </Section>

            <Section id="payments" title="7. Payment Processing">
              <p>
                Lender subscription payments ($55/month) are processed by <strong className="text-white">Stripe, Inc.</strong>,
                a PCI DSS Level 1 certified payment processor.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Kojiru does not receive, process, or store your full credit card number, CVV, or bank account details.</li>
                <li>Stripe provides Kojiru with a customer identifier, subscription status, payment confirmation, and the last four digits of your payment method for display purposes only.</li>
                <li>Stripe processes your payment data in accordance with their privacy policy at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">stripe.com/privacy</a>.</li>
              </ul>
              <p className="mt-3">
                Subscription fees are non-refundable. Cancellation takes effect at the end of the current billing period.
              </p>
            </Section>

            <Section id="sharing" title="8. Information Sharing & Disclosure">
              <p>We do not sell your personal information. We may share information in the following limited circumstances:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className="text-gray-300">Service Providers:</span> iDenfy (identity verification), Stripe (payment processing), and Google Cloud (AI processing) receive only the data necessary to perform their specific function.</li>
                <li><span className="text-gray-300">Platform Participants:</span> Lenders can see borrower loan request details (amount, purpose, term, and KYC verification status). Borrower names and personal contact information are not shared with lenders until a loan is matched.</li>
                <li><span className="text-gray-300">Legal Requirements:</span> We may disclose information when required by law, subpoena, court order, or government request, or when necessary to protect the rights, safety, or property of Kojiru, our users, or the public.</li>
                <li><span className="text-gray-300">Business Transfers:</span> In connection with a merger, acquisition, or sale of assets, user data may be transferred as a business asset, subject to the same privacy protections described in this policy.</li>
              </ul>
            </Section>

            <Section id="retention" title="9. Data Retention">
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Account Data:</span> Retained for as long as your account is active. Upon account deletion request, personal data is removed within 30 days, except where retention is required by law.</li>
                <li><span className="text-gray-300">KYC Records:</span> Retained for a minimum of 5 years after the business relationship ends, as required by the Bank Secrecy Act and related AML regulations.</li>
                <li><span className="text-gray-300">Transaction Records:</span> Loan records, payment transactions, and bid history are retained for 7 years for regulatory and audit purposes.</li>
                <li><span className="text-gray-300">AI Audit Logs:</span> Security audit logs (injection attempts, threat detection) are retained for 2 years.</li>
                <li><span className="text-gray-300">Blockchain Data:</span> On-chain data is permanent and cannot be deleted (see Section 6).</li>
              </ul>
            </Section>

            <Section id="security" title="10. Security">
              <p>We implement industry-standard security measures to protect your information:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Passwords are hashed using bcrypt with per-user salts. We never store plaintext passwords.</li>
                <li>API authentication uses JSON Web Tokens (JWT) with expiration enforcement.</li>
                <li>All data in transit is encrypted via TLS 1.2+.</li>
                <li>Smart contract ownership is secured by a 2-of-2 multi-signature wallet (Safe), eliminating single points of failure.</li>
                <li>AI endpoints enforce input sanitization, output validation, and automatic threat response (account flagging after 3+ injection attempts).</li>
                <li>Automated weekly security digests monitor for injection attempts, flagged users, and emerging threat patterns.</li>
                <li>Secrets management uses environment variables with pre-commit hooks (Gitleaks) to prevent accidental exposure.</li>
              </ul>
              <p className="mt-3">
                No method of electronic transmission or storage is 100% secure. While we strive to protect your
                information, we cannot guarantee absolute security.
              </p>
            </Section>

            <Section id="rights" title="11. Your Rights">
              <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Access:</span> Request a copy of the personal data we hold about you.</li>
                <li><span className="text-gray-300">Correction:</span> Request correction of inaccurate or incomplete data.</li>
                <li><span className="text-gray-300">Deletion:</span> Request deletion of your personal data, subject to legal retention requirements.</li>
                <li><span className="text-gray-300">Portability:</span> Request your data in a structured, machine-readable format.</li>
                <li><span className="text-gray-300">Objection:</span> Object to processing of your data for certain purposes.</li>
                <li><span className="text-gray-300">Withdrawal of Consent:</span> Withdraw consent for data processing where consent is the legal basis.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, contact us at the address below. We will respond within 30 days.
                Note that deletion of your account does not remove data already recorded on the blockchain.
              </p>
              <p className="mt-2">
                <strong className="text-gray-300">California Residents (CCPA):</strong> You have the right to know what personal information
                we collect, request deletion, and opt out of the sale of personal information. Kojiru does not sell personal information.
              </p>
            </Section>

            <Section id="cookies" title="12. Cookies & Tracking">
              <p>
                The Platform uses essential cookies and local storage to maintain your authentication session and
                user preferences. We do not use third-party advertising cookies or cross-site tracking technologies.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><span className="text-gray-300">Authentication Token:</span> Stored in local storage to maintain your login session.</li>
                <li><span className="text-gray-300">User Preferences:</span> Theme, notification settings, and dashboard layout preferences.</li>
                <li><span className="text-gray-300">Wallet Connection:</span> Web3 provider connection state for blockchain interactions.</li>
              </ul>
            </Section>

            <Section id="children" title="13. Children's Privacy">
              <p>
                The Platform is not intended for individuals under the age of 18. We do not knowingly collect
                personal information from children. If we become aware that we have inadvertently collected data
                from a minor, we will take steps to delete it promptly.
              </p>
            </Section>

            <Section id="public-ledger" title="14. Binding Action Gate: Public Ledger and Permanent Anchors">
              <GateLegalStatusNote testid="privacy-bag-status-note" />
              <p>Effective {BAG_LEGAL_EFFECTIVE}. A standalone copy is at <Link to="/binding-gate/privacy" className="text-blue-400 hover:underline" data-testid="privacy-bag-standalone-link">kojiru.com/binding-gate/privacy</Link>.</p>
              <BindingLegalList items={BAG_PRIVACY} testid="privacy-bag-list" />
            </Section>

            <Section id="changes" title="15. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Material changes will be communicated through
                a notice on the Platform or via email to registered users. The "Last Updated" date at the top of
                this page indicates when the policy was most recently revised. Continued use of the Platform after
                changes constitutes acceptance of the updated policy.
              </p>
            </Section>

            <Section id="contact" title="16. Contact Us">
              <p>If you have questions about this Privacy Policy or wish to exercise your data rights, contact us:</p>
              <div className="mt-3 p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                <p className="text-gray-300 font-medium">Kojiru Technologies, Inc.</p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400" />
                  <a href="mailto:privacy@kojiru.com" className="text-blue-400 hover:underline">privacy@kojiru.com</a>
                </p>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
